import React, { useState } from 'react';
import { Container, Row, Col, Card, Nav, Tab, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCardHeader from '../common/FalconCardHeader';
import UserManagement from './UserManagement';
import RoleManagement from './RoleManagement';
import AuditLog from './AuditLog';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const statsData = {
    users: {
      total: 18,
      active: 15,
      inactive: 3,
      admins: 2,
      newThisMonth: 4
    },
    roles: {
      total: 4,
      custom: 3,
      system: 1,
      permissions: 18
    },
    activity: {
      logins: 127,
      operations: 89,
      errors: 2,
      uptime: 99.8
    }
  };

  const recentActivities = [
    {
      id: 1,
      type: 'user_created',
      user: 'María González',
      action: 'Nuevo usuario creado',
      timestamp: '2024-01-15 14:30:00',
      icon: 'user-plus',
      color: 'success'
    },
    {
      id: 2,
      type: 'role_updated',
      user: 'Juan Rodriguez',
      action: 'Permisos del rol Editor actualizados',
      timestamp: '2024-01-15 12:15:00',
      icon: 'user-shield',
      color: 'warning'
    },
    {
      id: 3,
      type: 'user_login',
      user: 'Pedro Martínez',
      action: 'Inicio de sesión',
      timestamp: '2024-01-15 10:45:00',
      icon: 'sign-in-alt',
      color: 'info'
    },
    {
      id: 4,
      type: 'user_disabled',
      user: 'Ana López',
      action: 'Usuario deshabilitado',
      timestamp: '2024-01-14 16:20:00',
      icon: 'user-times',
      color: 'danger'
    }
  ];

  const systemAlerts = [
    {
      id: 1,
      type: 'warning',
      title: 'Usuarios sin actividad',
      message: '3 usuarios no han iniciado sesión en los últimos 30 días',
      action: 'Revisar usuarios inactivos'
    },
    {
      id: 2,
      type: 'info',
      title: 'Actualización disponible',
      message: 'Nueva versión del sistema disponible con mejoras de seguridad',
      action: 'Ver detalles'
    }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container fluid className="px-3">
      <div className="pb-4">
        {/* Header */}
        <div className="mb-4">
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-2">
                <FontAwesomeIcon icon="cogs" className="me-2 text-primary" />
                Panel de Administración
              </h2>
              <h5 className="text-700 fw-semi-bold">
                Gestión completa del sistema Galaxy Metrics
              </h5>
            </Col>
          </Row>
        </div>

        {/* Tabs de Navegación */}
        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          <Card className="shadow-sm">
            <Card.Header className="bg-light border-bottom-0">
              <Nav variant="tabs" className="card-header-tabs">
                <Nav.Item>
                  <Nav.Link eventKey="overview">
                    <FontAwesomeIcon icon="chart-bar" className="me-1" />
                    Resumen General
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="users">
                    <FontAwesomeIcon icon="users" className="me-1" />
                    Usuarios
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="roles">
                    <FontAwesomeIcon icon="user-shield" className="me-1" />
                    Roles y Permisos
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="audit">
                    <FontAwesomeIcon icon="list" className="me-1" />
                    Auditoría
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="settings">
                    <FontAwesomeIcon icon="cog" className="me-1" />
                    Configuración
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>

            <Tab.Content>
              {/* Tab Overview */}
              <Tab.Pane eventKey="overview">
                <Card.Body className="p-4">
                  {/* Estadísticas Principales */}
                  <Row className="g-3 mb-4">
                    <Col xl={3} md={6}>
                      <Card className="bg-gradient-primary text-white border-0">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h3 className="text-white mb-0">{statsData.users.total}</h3>
                              <small className="opacity-75">Total Usuarios</small>
                              <div className="mt-1">
                                <Badge bg="light" text="dark" className="fs-11">
                                  +{statsData.users.newThisMonth} este mes
                                </Badge>
                              </div>
                            </div>
                            <FontAwesomeIcon icon="users" size="2x" className="opacity-50" />
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col xl={3} md={6}>
                      <Card className="bg-gradient-success text-white border-0">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h3 className="text-white mb-0">{statsData.users.active}</h3>
                              <small className="opacity-75">Usuarios Activos</small>
                              <div className="mt-1">
                                <Badge bg="light" text="dark" className="fs-11">
                                  {((statsData.users.active / statsData.users.total) * 100).toFixed(1)}% activos
                                </Badge>
                              </div>
                            </div>
                            <FontAwesomeIcon icon="check-circle" size="2x" className="opacity-50" />
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col xl={3} md={6}>
                      <Card className="bg-gradient-warning text-white border-0">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h3 className="text-white mb-0">{statsData.roles.total}</h3>
                              <small className="opacity-75">Roles Configurados</small>
                              <div className="mt-1">
                                <Badge bg="light" text="dark" className="fs-11">
                                  {statsData.roles.permissions} permisos
                                </Badge>
                              </div>
                            </div>
                            <FontAwesomeIcon icon="user-shield" size="2x" className="opacity-50" />
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col xl={3} md={6}>
                      <Card className="bg-gradient-info text-white border-0">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h3 className="text-white mb-0">{statsData.activity.uptime}%</h3>
                              <small className="opacity-75">Uptime del Sistema</small>
                              <div className="mt-1">
                                <Badge bg="light" text="dark" className="fs-11">
                                  {statsData.activity.logins} sesiones hoy
                                </Badge>
                              </div>
                            </div>
                            <FontAwesomeIcon icon="cog" size="2x" className="opacity-50" />
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row className="g-3">
                    {/* Actividad Reciente */}
                    <Col lg={8}>
                      <Card className="h-100">
                        <FalconCardHeader 
                          title="Actividad Reciente" 
                          endEl={
                            <Button variant="falcon-default" size="sm" onClick={() => setActiveTab('audit')}>
                              <FontAwesomeIcon icon="list" className="me-1" />
                              Ver todo
                            </Button>
                          }
                        />
                        <Card.Body className="p-0">
                          <div className="bg-light px-3 py-2 border-bottom">
                            <small className="text-muted fw-semibold">ÚLTIMAS 24 HORAS</small>
                          </div>
                          {recentActivities.map((activity) => (
                            <div key={activity.id} className="px-3 py-3 border-bottom">
                              <div className="d-flex align-items-center">
                                <div className={`me-3 text-${activity.color}`}>
                                  <FontAwesomeIcon icon={activity.icon} size="lg" />
                                </div>
                                <div className="flex-1">
                                  <h6 className="mb-1 fw-semibold">{activity.action}</h6>
                                  <div className="d-flex align-items-center">
                                    <small className="text-muted me-2">por {activity.user}</small>
                                    <Badge bg={activity.color} className="fs-11 opacity-75">
                                      {formatDate(activity.timestamp)}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Alertas del Sistema */}
                    <Col lg={4}>
                      <Card className="h-100">
                        <FalconCardHeader title="Alertas del Sistema" />
                        <Card.Body>
                          {systemAlerts.map((alert) => (
                            <div key={alert.id} className={`alert alert-${alert.type} border-0 mb-3`}>
                              <div className="d-flex align-items-start">
                                <FontAwesomeIcon 
                                  icon={alert.type === 'warning' ? 'exclamation-triangle' : 'info-circle'} 
                                  className="me-2 mt-1" 
                                />
                                <div className="flex-1">
                                  <h6 className="alert-heading mb-1">{alert.title}</h6>
                                  <p className="mb-2 fs-11">{alert.message}</p>
                                  <Button variant={`outline-${alert.type}`} size="sm">
                                    {alert.action}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Acciones Rápidas */}
                          <div className="bg-light p-3 rounded mt-4">
                            <h6 className="mb-3">
                              <FontAwesomeIcon icon="plus" className="me-1 text-warning" />
                              Acciones Rápidas
                            </h6>
                            <div className="d-grid gap-2">
                              <Button variant="outline-primary" size="sm" onClick={() => setActiveTab('users')}>
                                <FontAwesomeIcon icon="user-plus" className="me-1" />
                                Crear Usuario
                              </Button>
                              <Button variant="outline-secondary" size="sm" onClick={() => setActiveTab('roles')}>
                                <FontAwesomeIcon icon="cog" className="me-1" />
                                Nuevo Rol
                              </Button>
                              <Button variant="outline-info" size="sm" onClick={() => setActiveTab('audit')}>
                                <FontAwesomeIcon icon="download" className="me-1" />
                                Exportar Logs
                              </Button>
                              <Button 
                                variant="outline-success" 
                                size="sm" 
                                onClick={() => window.location.href = '/admin/fee-configurations'}
                              >
                                <FontAwesomeIcon icon="user-tie" className="me-1" />
                                Configurar Fee Admin
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card.Body>
              </Tab.Pane>

              {/* Tab Usuarios */}
              <Tab.Pane eventKey="users">
                <UserManagement />
              </Tab.Pane>

              {/* Tab Roles */}
              <Tab.Pane eventKey="roles">
                <RoleManagement />
              </Tab.Pane>

              {/* Tab Auditoría */}
              <Tab.Pane eventKey="audit">
                <AuditLog />
              </Tab.Pane>

              {/* Tab Configuración */}
              <Tab.Pane eventKey="settings">
                <Card.Body className="p-4">
                  <Row className="g-4">
                    <Col lg={6}>
                      <Card className="border-0 bg-light">
                        <Card.Body>
                          <h5 className="mb-3">
                            <FontAwesomeIcon icon="cog" className="me-2 text-primary" />
                            Configuración de Seguridad
                          </h5>
                          <div className="mb-3">
                            <label className="form-label fw-semibold">Tiempo de sesión (minutos)</label>
                            <input type="number" className="form-control" defaultValue={60} />
                          </div>
                          <div className="mb-3">
                            <label className="form-label fw-semibold">Intentos de login máximos</label>
                            <input type="number" className="form-control" defaultValue={3} />
                          </div>
                          <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" defaultChecked />
                            <label className="form-check-label fw-semibold">
                              Requerir autenticación de dos factores
                            </label>
                          </div>
                          <Button variant="primary">
                            <FontAwesomeIcon icon="check" className="me-1" />
                            Guardar Configuración
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col lg={6}>
                      <Card className="border-0 bg-light">
                        <Card.Body>
                          <h5 className="mb-3">
                            <FontAwesomeIcon icon="cog" className="me-2 text-secondary" />
                            Configuración del Sistema
                          </h5>
                          <div className="mb-3">
                            <label className="form-label fw-semibold">Nombre de la aplicación</label>
                            <input type="text" className="form-control" defaultValue="Galaxy Metrics" />
                          </div>
                          <div className="mb-3">
                            <label className="form-label fw-semibold">Email de soporte</label>
                            <input type="email" className="form-control" defaultValue="support@galaxymetrics.com" />
                          </div>
                          <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" defaultChecked />
                            <label className="form-check-label fw-semibold">
                              Habilitar notificaciones por email
                            </label>
                          </div>
                          <Button variant="secondary">
                            <FontAwesomeIcon icon="check" className="me-1" />
                            Guardar Configuración
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card.Body>
              </Tab.Pane>
            </Tab.Content>
          </Card>
        </Tab.Container>
      </div>
    </Container>
  );
};

export default AdminDashboard; 